/**
 * Base Provider Interface
 * 
 * Abstract base class that defines the contract all analytics providers must implement.
 * All providers should extend this class and implement the required methods.
 */

class BaseProvider {
  /**
   * Initialize the analytics provider with configuration
   * @param {Object} config - Provider-specific configuration object
   * @throws {Error} Must be implemented by subclass
   */
  init(config) {
    throw new Error('init() must be implemented by provider subclass');
  }

  /**
   * Identify a user with unique ID and optional traits
   * @param {string} userId - Unique identifier for the user
   * @param {Object} properties - Optional user properties/traits
   * @throws {Error} Must be implemented by subclass
   */
  identify(userId, properties = {}) {
    throw new Error('identify() must be implemented by provider subclass');
  }

  /**
   * Track a custom event with optional properties
   * @param {string} eventName - Name of the event to track
   * @param {Object} properties - Optional event properties
   * @throws {Error} Must be implemented by subclass
   */
  track(eventName, properties = {}) {
    throw new Error('track() must be implemented by provider subclass');
  }

  /**
   * Track a page/screen view
   * @param {string} pageName - Name of the page/screen
   * @param {Object} properties - Optional page properties
   * @throws {Error} Must be implemented by subclass
   */
  page(pageName, properties = {}) {
    throw new Error('page() must be implemented by provider subclass');
  }

  /**
   * Reset user session (clears user ID, typically on logout)
   * @throws {Error} Must be implemented by subclass
   */
  reset() {
    throw new Error('reset() must be implemented by provider subclass');
  }

  /**
   * Set or update user properties without identifying
   * @param {Object} properties - User properties to set
   * @throws {Error} Must be implemented by subclass
   */
  setUserProperties(properties) {
    throw new Error('setUserProperties() must be implemented by provider subclass');
  }

  /**
   * Associate user with a group/organization (B2B use case)
   * @param {string} groupId - Unique identifier for the group
   * @param {Object} traits - Optional group properties
   * @throws {Error} Must be implemented by subclass
   */
  group(groupId, traits = {}) {
    throw new Error('group() must be implemented by provider subclass');
  }

  /**
   * Merge two user identities (e.g., anonymous → identified)
   * @param {string} newId - New user identifier
   * @param {string} originalId - Original user identifier (optional)
   * @throws {Error} Must be implemented by subclass
   */
  alias(newId, originalId) {
    throw new Error('alias() must be implemented by provider subclass');
  }

  /**
   * Immediately send all queued events to server (useful before page unload)
   * @throws {Error} Must be implemented by subclass
   */
  flush() {
    throw new Error('flush() must be implemented by provider subclass');
  }

  /**
   * Check if a feature flag is enabled (provider-specific)
   * @param {string} flagName - Name of the feature flag
   * @returns {boolean|null} - Returns true/false if supported, null if not supported
   */
  isFeatureEnabled(flagName) {
    // Optional method - return null if not supported
    return null;
  }

  /**
   * Get feature flag value with optional payload (provider-specific)
   * @param {string} flagName - Name of the feature flag
   * @returns {any|null} - Returns flag value if supported, null if not supported
   */
  getFeatureFlag(flagName) {
    // Optional method - return null if not supported
    return null;
  }
}

export default BaseProvider;

